import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { usePanel } from 'hooks/usePanel';
import { borrowerGuaranteeFormConfig, BorrowerGuaranteeFormValues } from './config/BorrowerGuaranteeFormConfig';
import { BorrowerGuaranteeLayout } from './components/BorrowerGuaranteeLayout';
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from '../../contexts/main-borrower-pricing/MainBorrowerPricingContext';
import { LoanConfigurationDto } from 'api/borrower';

interface BorrowerGuaranteeFormProps {
  loanConfiguration: LoanConfigurationDto
}

export const BorrowerGuaranteeForm: React.FC<BorrowerGuaranteeFormProps> = ({ loanConfiguration }) => {
  const { insured, pendingLoanConfigs } = useMainBorrowerPricingState()
  const { onSearch } = useMainBorrowerPricingActions()
  const { loan } = loanConfiguration
  const formikRef = useRef<FormikProps<BorrowerGuaranteeFormValues>>(null);
  const { setPanelActions, closePanel } = usePanel()

  const currentPendingLoan = useMemo(() => {
    return pendingLoanConfigs.find(config => config.loanId === loan.id)
  }, [pendingLoanConfigs, loan])

  const initialValues: BorrowerGuaranteeFormValues = useMemo(() => {
    const defaults = borrowerGuaranteeFormConfig.initialValues
    if (currentPendingLoan) {
      const { borrowerRoleId, loanId, isActive, ...rest } = currentPendingLoan
      return {
        ...defaults,
        ...rest,
        // S'assurer que les valeurs nullish utilisent les valeurs par défaut
        deductible: rest.deductible ?? defaults.deductible,
        quota: rest.quota ?? defaults.quota,
        isPsyBuyback: rest.isPsyBuyback ?? defaults.isPsyBuyback,
      } as BorrowerGuaranteeFormValues
    }
    return defaults
  }, [currentPendingLoan]);

  const handleSubmit = useCallback(async (
    values: BorrowerGuaranteeFormValues
  ) => {
    const index = pendingLoanConfigs.findIndex((item) => item.loanId === loan.id)
    if (index !== -1) {
      const updatedConfigs = [...pendingLoanConfigs]
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        ...values
      }
      onSearch(updatedConfigs)
    }
    closePanel()
  }, [pendingLoanConfigs, onSearch, loan, closePanel]);

  const deleteLoanBorrower = useCallback(() => {
    const newPendingLoanConfigs = pendingLoanConfigs.filter(config => config.loanId !== loan.id)
    onSearch(newPendingLoanConfigs)
  }, [pendingLoanConfigs, onSearch, loan]);

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions(
        {
          closeLabel: "COMMON.CANCEL",
          submitLabel: "COMMON.VALIDATE",
          onSubmit: () => {
            formikRef.current?.submitForm()
          },
          onDelete: () => {
            deleteLoanBorrower()
            closePanel()
          },
          deleteLabel: "COMMON.DELETE"
        }
      )
    }
  }, [closePanel, setPanelActions, deleteLoanBorrower])

  return (
    <div className="flex items-center justify-between mb-4">
      <div className='w-full flex flex-col gap-4'>
        <Formik
          innerRef={formikRef}
          key={`${insured?.id}-${loan.id}`}
          initialValues={initialValues}
          validationSchema={borrowerGuaranteeFormConfig.validationSchema}
          onSubmit={handleSubmit}
        >
          <Form>
            <BorrowerGuaranteeLayout loan={loan} />
          </Form>
        </Formik>
      </div>
    </div>
  );
};
